import { useEffect, useMemo, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";

/**
 * Flat multi-select dropdown with the same look & feel as
 * ServiceCategoryV2Dropdown, but for arbitrary {value, label} options.
 *
 * Controlled:
 *   - options: Array<{ value, label }>
 *   - selected: Array<{ value, label, ...extras }>
 *   - onChange: (next) => void
 *   - placeholder: optional placeholder
 *   - lockedValue: optional value that cannot be removed
 */
export default function BadgeMultiSelect({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select...",
  lockedValue = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    const q = searchQuery.toLowerCase();
    return options.filter((o) =>
      (o.label || "").toString().toLowerCase().includes(q)
    );
  }, [options, searchQuery]);

  const dedupeByLabel = (arr) => {
    const seen = new Set();
    const result = [];
    for (const item of arr || []) {
      const key = (item?.label ?? item?.value ?? "").toString().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
    return result;
  };

  const emit = (next) => {
    if (onChange) onChange(dedupeByLabel(next));
  };

  const handleToggle = (option) => {
    if (option.value === lockedValue) return;
    const isSelected = selected.some((sc) => sc.value === option.value);
    if (isSelected) {
      emit(selected.filter((sc) => sc.value !== option.value));
    } else {
      emit([...selected, option]);
    }
  };

  const removeTag = (value) => {
    if (value === lockedValue) return;
    emit(selected.filter((sc) => sc.value !== value));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="relative w-full cursor-pointer rounded-md bg-white py-1.5 pl-3 pr-10 text-left outline outline-1 -outline-offset-1 outline-gray-300 focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-indigo-600 sm:text-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap items-center gap-1 min-h-[24px]">
          {selected.length > 0 ? (
            dedupeByLabel(selected)
              .filter((tag) => tag && (tag.value != null || tag.label != null))
              .map((tag) => {
                const tagValue =
                  typeof tag.value === "string" || typeof tag.value === "number"
                    ? tag.value
                    : "";
                const tagLabel =
                  typeof tag.label === "string"
                    ? tag.label
                    : typeof tag.value === "string"
                      ? tag.value
                      : "";
                const isLocked = tagValue === lockedValue;
                return (
                  <span
                    key={tagValue}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                    style={{
                      backgroundColor: "#EEF2FF",
                      color: "#4338CA",
                      "--tw-ring-color": "#4338CA33",
                    }}
                  >
                    {tagLabel}
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeTag(tagValue);
                        }}
                        className="ml-0.5 inline-flex items-center hover:opacity-70"
                      >
                        <XMarkIcon className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                );
              })
          ) : (
            <span className="text-sm text-gray-400">{placeholder}</span>
          )}
        </div>
        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </div>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
          <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                className="w-full rounded-md border-0 bg-gray-50 py-1.5 pl-8 pr-3 text-sm text-gray-900 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 focus:outline-none"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
          <div
            className="max-h-[26rem] overflow-y-auto py-1 px-3"
            style={{ paddingBottom: "1rem" }}
          >
            <ul className="space-y-0">
              {filteredOptions.map((option, idx) => {
                const isChecked = selected.some(
                  (sc) => sc.value === option.value
                );
                return (
                  <li
                    key={`opt-${option.value ?? idx}`}
                    className={`relative select-none py-2 pl-3 pr-9 cursor-pointer rounded-md ${
                      isChecked ? "bg-indigo-50" : "hover:bg-indigo-50"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggle(option);
                    }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: "#4338CA" }}
                      />
                      <span
                        className={`text-sm leading-5 truncate ${
                          isChecked ? "font-semibold text-gray-900" : "font-normal text-gray-700"
                        }`}
                        title={option.label}
                      >
                        {option.label}
                      </span>
                    </div>
                    {isChecked && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <svg
                          className="h-5 w-5 text-indigo-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
